'use client'

import { ComponentPropsWithoutRef, ComponentRef, forwardRef, useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { ExternalLink, LogOut, Settings, User, UserCircle } from 'lucide-react'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { useMutation } from '@tanstack/react-query'
import { useMeStore, useLogoutStore } from '@/store'
import { getErrorMessage } from '@/lib/utils'
import { hasAnyPermission } from '@/lib/permissions'
import { UserAvatar } from '@/components/partials'
import config from '@/config'

const AppUserDropdown = forwardRef<ComponentRef<typeof DropdownMenu>, ComponentPropsWithoutRef<typeof DropdownMenu>>(
  ({ children, ...props }, _) => {
    const t = useTranslations()
    const router = useRouter()
    const [open, setOpen] = useState(false)
    const { meState, reset: resetMeStore } = useMeStore()
    const { response: userData, isLoading, error } = meState

    const { mutate: logoutMutate, isPending: isLoggingOut } = useMutation({
      mutationFn: async () => {
        const logoutStore = useLogoutStore.getState()
        return await logoutStore.logout()
      },
      onSuccess: data => {
        resetMeStore()
        toast.success(data.message)
        router.push('/login')
      },
      onError: (error: any) => {
        toast.error(getErrorMessage(error, t('something_went_wrong')))
      }
    })

    useEffect(() => {
      if (error !== null) {
        const errorMessage = getErrorMessage(error.message, t('error_loading_record'))
        toast.error(errorMessage)
        console.error('Error loading user data:', error)
      }
    }, [error, t])

    const navigate = (href: string) => {
      setOpen(false)
      router.push(href)
    }

    // Check if the user has any of the Settings permissions
    const hasSettingsAccess = hasAnyPermission(userData, [
      ['SETTINGS', 'GENERAL_SHOW'],
      ['SETTINGS', 'CONTENT_SHOW'],
      ['SETTINGS', 'SYSTEM']
    ])

    if (userData === null) {
      return <></>
    }

    return (
      <DropdownMenu open={open} onOpenChange={setOpen} {...props}>
        <DropdownMenuTrigger asChild>
          {children || (
            <Button variant="outline" size="icon">
              <User className="h-4 w-4" />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="border-sidebar-border w-56">
          <DropdownMenuItem
            onSelect={e => {
              e.preventDefault()
              navigate('/profile')
            }}
            className="cursor-pointer p-2">
            <UserAvatar user={userData} size="sm" />
            <div>
              {isLoading ? (
                <p className="text-sm font-medium">{t('loading')}...</p>
              ) : error ? (
                <p className="text-sm font-medium">{getErrorMessage(error, t('error_loading_record'))}</p>
              ) : (
                <>
                  <p className="text-sm font-medium">{userData.fullName || 'Unknown User'}</p>
                  <p className="text-muted-foreground text-xs">{userData.email || 'No email'}</p>
                </>
              )}
            </div>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={e => {
              e.preventDefault()
              navigate('/profile')
            }}>
            <UserCircle className="h-4 w-4" />
            <span>{t('profile')}</span>
          </DropdownMenuItem>
          {hasSettingsAccess && (
            <DropdownMenuItem
              onSelect={e => {
                e.preventDefault()
                navigate('/settings')
              }}>
              <Settings className="h-4 w-4" />
              <span>{t('settings')}</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onSelect={e => {
              e.preventDefault()
              setOpen(false)
              window.open(config.APP_BASE_URL, '_blank', 'noopener,noreferrer')
            }}>
            <ExternalLink className="h-4 w-4" />
            <span>{t('go_to_app')}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-red-600 dark:text-red-400"
            onSelect={e => {
              e.preventDefault()
              logoutMutate()
            }}
            disabled={isLoggingOut}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>{isLoggingOut ? `${t('loading')}...` : t('logout')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }
)

AppUserDropdown.displayName = 'UserDropdown'

export default AppUserDropdown
